import { useCallback, useEffect, useRef } from 'react';

interface UseIdleTimerOptions {
  timeout: number; // milliseconds
  onIdle: () => void;
}

interface UseIdleTimerReturn {
  reset: () => void;
}

export function useIdleTimer({
  timeout,
  onIdle,
}: UseIdleTimerOptions): UseIdleTimerReturn {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onIdleRef = useRef(onIdle);

  // Keep onIdle callback up to date
  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  const clearExistingTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const startTimeout = useCallback(() => {
    clearExistingTimeout();
    timeoutRef.current = setTimeout(() => {
      onIdleRef.current();
    }, timeout);
  }, [clearExistingTimeout, timeout]);

  const resetTimer = useCallback(() => {
    startTimeout();
  }, [startTimeout]);

  // Track user activity
  useEffect(() => {
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    const handleActivity = () => {
      resetTimer();
    };

    // Start initial timeout
    startTimeout();

    // Add event listeners
    for (const event of events) {
      document.addEventListener(event, handleActivity, { passive: true });
    }

    return () => {
      // Cleanup
      clearExistingTimeout();
      for (const event of events) {
        document.removeEventListener(event, handleActivity);
      }
    };
  }, [resetTimer, startTimeout, clearExistingTimeout]);

  return { reset: resetTimer };
}
